import {
  collection, getDocs, getDoc, getCountFromServer,
  addDoc, doc, updateDoc, deleteDoc,
  query, where, orderBy, Timestamp
} from "firebase/firestore";
import { db, storage } from "../config/firebase";
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import DOMPurify from 'dompurify';
import * as geolib from 'geolib';
import { searchCity } from './citySearch';


const EVENTS_CACHE_VERSION = "v2";
const EVENTS_CACHE_KEY = `eventsCache:${EVENTS_CACHE_VERSION}`;
const CACHE_EXPIRY = 2 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_MAX_EVENTS_FALLBACK = 120;
const CACHE_MAX_DESCRIPTION_LENGTH = 1200;

const isCacheValid = (timestamp) => {
  const now = new Date().getTime();
  return now - timestamp < CACHE_EXPIRY;
};

// New cache access helper function
const getCachedEvents = () => {
  let cachedData = null;
  try {
    cachedData = localStorage.getItem(EVENTS_CACHE_KEY);
  } catch (error) {
    return null;
  }
  if (!cachedData) return null;
  try {
    const { eventsData, timestamp, truncated } = JSON.parse(cachedData);
    if (truncated) {
      return null;
    }
    return (eventsData && isCacheValid(timestamp)) ? eventsData : null;
  } catch (e) {
    return null;
  }
};

const compactEventForCache = (event) => {
  if (!event || typeof event !== 'object') return event;

  const compact = { ...event };
  if (typeof compact.description === 'string' && compact.description.length > CACHE_MAX_DESCRIPTION_LENGTH) {
    compact.description = compact.description.slice(0, CACHE_MAX_DESCRIPTION_LENGTH);
  }
  if (typeof compact.raw === 'string') {
    delete compact.raw;
  }
  return compact;
};

const writeEventsCache = (eventsData) => {
  if (!Array.isArray(eventsData)) return;

  const buildPayload = (list, truncated = false) => JSON.stringify({
    eventsData: list,
    timestamp: new Date().getTime(),
    truncated
  });

  const primaryList = eventsData.map(compactEventForCache);

  try {
    localStorage.setItem(EVENTS_CACHE_KEY, buildPayload(primaryList, false));
    return;
  } catch (error) {
    if (error?.name !== 'QuotaExceededError') {
      console.warn('Failed to persist events cache:', error);
      return;
    }
  }

  try {
    const fallbackList = eventsData.slice(0, CACHE_MAX_EVENTS_FALLBACK).map(event => {
      const compact = compactEventForCache(event);
      if (compact && typeof compact === 'object') {
        return {
          ...compact,
          description: typeof compact.description === 'string'
            ? compact.description.slice(0, 300)
            : compact.description,
        };
      }
      return compact;
    });
    localStorage.removeItem(EVENTS_CACHE_KEY);
    localStorage.setItem(EVENTS_CACHE_KEY, buildPayload(fallbackList, true));
    console.info('Events cache stored in truncated mode; it will be ignored for full list loads.');
  } catch (error) {
    console.warn('Events cache skipped due to storage quota limits.');
  }
};

const isValidLatitude = (value) => Number.isFinite(value) && value >= -90 && value <= 90;
const isValidLongitude = (value) => Number.isFinite(value) && value >= -180 && value <= 180;

const buildCoordinateCandidates = (geo) => {
  if (!geo) return [];

  if (typeof geo === 'object') {
    const lat = Number(geo.lat ?? geo.latitude);
    const lng = Number(geo.lng ?? geo.lon ?? geo.longitude);
    if (isValidLatitude(lat) && isValidLongitude(lng)) {
      return [{ latitude: lat, longitude: lng }];
    }
    return [];
  }

  if (typeof geo === 'string') {
    const parts = geo.split(',').map(part => Number(String(part).trim()));
    if (parts.length !== 2 || parts.some(part => Number.isNaN(part))) {
      return [];
    }

    const [first, second] = parts;
    const candidates = [];

    if (isValidLatitude(first) && isValidLongitude(second)) {
      candidates.push({ latitude: first, longitude: second });
    }
    if (isValidLatitude(second) && isValidLongitude(first)) {
      const swapped = { latitude: second, longitude: first };
      const alreadyPresent = candidates.some(c => c.latitude === swapped.latitude && c.longitude === swapped.longitude);
      if (!alreadyPresent) {
        candidates.push(swapped);
      }
    }

    return candidates;
  }

  return [];
};

const resolveCoordinates = (geo, referenceCoords = null) => {
  const candidates = buildCoordinateCandidates(geo);
  if (!candidates.length) return null;
  if (!referenceCoords || candidates.length === 1) return candidates[0];

  return candidates.reduce((bestCandidate, candidate) => {
    const currentDistance = geolib.getDistance(referenceCoords, candidate);
    const bestDistance = geolib.getDistance(referenceCoords, bestCandidate);
    return currentDistance < bestDistance ? candidate : bestCandidate;
  });
};

const normalizePostalCode = (value) => String(value || '').trim().replace(/\s+/g, '');
const normalizeCityName = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const areCityNamesCompatible = (searchedCity, eventCity) => {
  if (!searchedCity || !eventCity) return false;
  if (searchedCity === eventCity) return true;
  return searchedCity.includes(eventCity) || eventCity.includes(searchedCity);
};

const parseCityInput = (city) => {
  if (!city) return null;
  if (typeof city === 'object') return city;

  const cityText = String(city).trim();
  if (!cityText) return null;

  const zipMatch = cityText.match(/^(.*?)\s*\((\d{5})\)\s*$/);
  if (zipMatch) {
    return {
      cityName: zipMatch[1].trim(),
      postalCode: zipMatch[2],
    };
  }

  return { cityName: cityText };
};

const eventMatchesCity = (event, parsedCity) => {
  if (!parsedCity?.cityName || !event?.location) return false;

  const searchedCity = normalizeCityName(parsedCity.cityName);
  const searchedPostal = normalizePostalCode(parsedCity.postalCode);
  const eventCity = normalizeCityName(event.location.cityName);
  const eventPostal = normalizePostalCode(event.location.postalCode);

  if (!areCityNamesCompatible(searchedCity, eventCity)) return false;
  if (searchedPostal && searchedPostal !== eventPostal) return false;

  return true;
};

const getCitySortPriority = (event, parsedCity) => {
  if (!event?.location || !parsedCity?.cityName) return 2;

  const searchedCity = normalizeCityName(parsedCity.cityName);
  const searchedPostal = normalizePostalCode(parsedCity.postalCode);
  const eventCity = normalizeCityName(event?.location?.cityName);
  const eventPostal = normalizePostalCode(event?.location?.postalCode);

  const cityMatches = areCityNamesCompatible(searchedCity, eventCity);
  if (!cityMatches) return 2;

  if (!searchedPostal) return 0;
  return searchedPostal === eventPostal ? 0 : 1;
};

const compareEventsByDefaultOrder = (a, b) => {
  const aDate = (a?.endDate instanceof Date ? a.endDate : (a?.date instanceof Date ? a.date : new Date(8640000000000000)));
  const bDate = (b?.endDate instanceof Date ? b.endDate : (b?.date instanceof Date ? b.date : new Date(8640000000000000)));
  return aDate - bDate;
};

const sortEventsForCitySearch = (eventsData, city) => {
  const parsedCity = parseCityInput(city);

  eventsData.sort((a, b) => {
    const cityPriorityA = getCitySortPriority(a, parsedCity);
    const cityPriorityB = getCitySortPriority(b, parsedCity);
    if (cityPriorityA !== cityPriorityB) return cityPriorityA - cityPriorityB;

    const bothInSearchedCity = cityPriorityA < 2 && cityPriorityB < 2;
    if (bothInSearchedCity) {
      return compareEventsByDefaultOrder(a, b);
    }

    const distanceA = Number.isFinite(a?.distanceInMeters) ? a.distanceInMeters : Number.POSITIVE_INFINITY;
    const distanceB = Number.isFinite(b?.distanceInMeters) ? b.distanceInMeters : Number.POSITIVE_INFINITY;
    if (distanceA !== distanceB) return distanceA - distanceB;

    return compareEventsByDefaultOrder(a, b);
  });
};

const logEventsSearchSummary = ({ city, distance, restrictToCity, useCache, totalAfterFilters }) => {
  const parsedCity = parseCityInput(city);
  const cityName = parsedCity?.cityName || 'none';
  const postalCode = parsedCity?.postalCode || 'none';
  const distanceKm = Number.isFinite(distance) ? distance : 'none';

  console.info(
    `[events] filters city="${cityName}" postal="${postalCode}" distanceKm=${distanceKm} restrictToCity=${Boolean(restrictToCity)} cache=${Boolean(useCache)} results=${totalAfterFilters}`
  );
};

// New helper for city parameter processing
const processCityParameter = async (eventsData, city, distance, restrictToCity = false) => {
  const parsedCity = parseCityInput(city);
  if (!parsedCity) return eventsData;

  const cityName = String(parsedCity.cityName || '').trim();
  const postalCode = normalizePostalCode(parsedCity.postalCode);
  if (!cityName) return eventsData;

  let userLocation = parsedCity;
  let referenceCoords = resolveCoordinates(parsedCity.geo);

  if (!referenceCoords) {
    const cityCandidates = await searchCity(cityName, postalCode || null);
    const matchingCandidate = cityCandidates.find(candidate => {
      const sameName = areCityNamesCompatible(normalizeCityName(candidate.cityName), normalizeCityName(cityName));
      const samePostal = postalCode ? normalizePostalCode(candidate.postalCode) === postalCode : true;
      return sameName && samePostal;
    }) || cityCandidates[0];

    if (matchingCandidate) {
      referenceCoords = resolveCoordinates(matchingCandidate.geo);
      userLocation = {
        ...matchingCandidate,
        cityName,
        postalCode: postalCode || matchingCandidate.postalCode,
      };
    }
  }

  if (!referenceCoords) {
    const matchingEvent = eventsData.find(event => {
      if (!event.location) return false;
      const sameName = areCityNamesCompatible(normalizeCityName(event.location.cityName), normalizeCityName(cityName));
      const samePostal = postalCode ? normalizePostalCode(event.location.postalCode) === postalCode : true;
      return sameName && samePostal;
    });

    if (matchingEvent?.location?.geo) {
      const fallbackReference = referenceCoords || null;
      const eventCoords = resolveCoordinates(matchingEvent.location.geo, fallbackReference);
      if (eventCoords) {
        referenceCoords = eventCoords;
        userLocation = {
          ...matchingEvent.location,
          cityName,
          postalCode: postalCode || matchingEvent.location.postalCode,
        };
      }
    }
  }
  
  if (restrictToCity) {
    eventsData = eventsData.filter(event => eventMatchesCity(event, parsedCity));
  }

  if (distance && userLocation && userLocation.geo) {
    const userCoords = resolveCoordinates(userLocation.geo, referenceCoords || null);

    if (!userCoords) {
      console.warn('Invalid user geo format:', userLocation.geo);
      return eventsData;
    }
    
    // Calculate distance for each event and add it as a property
    eventsData = eventsData.map(event => {
      let distanceInMeters = Infinity;
      if (event.location && event.location.geo) {
        try {
          const eventLocation = resolveCoordinates(event.location.geo, userCoords);
          if (eventLocation) {
            distanceInMeters = geolib.getDistance(userCoords, eventLocation);
          }
        } catch (error) {
          console.warn('Invalid geo format for event:', event.id, event.location.geo);
        }
      }
      return { ...event, distanceInMeters };
    });
    
    // Filter events within the distance radius
    eventsData = eventsData.filter(event => 
      event.distanceInMeters <= distance * 1000
    );
  }
  
  return eventsData;
};

const toDateOrNull = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isUpcomingEvent = (event, startingDate) => {
  if (event?.published !== true) return false;

  const eventDate = toDateOrNull(event?.date);
  const eventEndDate = toDateOrNull(event?.endDate);
  const publishedUntil = toDateOrNull(event?.publishedUntil);

  const hasUpcomingPreciseDate = eventEndDate
    ? eventEndDate >= startingDate
    : (eventDate ? eventDate >= startingDate : false);

  if (hasUpcomingPreciseDate) return true;

  const isNonPreciseNotice = event?.hasPreciseDate === false;
  if (!isNonPreciseNotice) return false;

  if (publishedUntil) {
    return publishedUntil >= new Date();
  }

  return Boolean(event?.presentInMawaqit);
};

export const getSelectedEvents = async (eventIds) => {
  try {

    // Check if data is available in cache
    const cachedData = localStorage.getItem(EVENTS_CACHE_KEY);

    if (cachedData) {
      const { eventsData: cachedEventsData, timestamp } = JSON.parse(cachedData);
      const eventsData = cachedEventsData || [];

      if (eventsData && isCacheValid(timestamp)) {
        console.log('Using cached favorite events data');

        // remove null and undefined values from cache
        const filteredEventsData = eventsData.filter(event => event !== null && event !== undefined);

        // check if all eventIds are in the cache
        const allEventIdsInCache = eventIds.every(id => filteredEventsData.map(event => event?.id).includes(id));
        if (allEventIdsInCache) {
          const favoriteEvents = filteredEventsData.filter(event => eventIds.includes(event.id) && event?.published === true);

          // Convert date strings to JavaScript Date objects
          favoriteEvents.forEach(event => {
            event.date = new Date(event.date);
            if (event.endDate) {
              event.endDate = new Date(event.endDate);
            }
          });
          return favoriteEvents;
        } else {
          console.log('Not all events are in cache, fetching from server...');
        }
      }
    }
    
    // Fetch from Firestore the events that match the provided eventIds when event exists
    let favoriteEvents = await Promise.all(eventIds.map(async eventId => {
      const eventDoc = await getDoc(doc(db, 'events', eventId));
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        if (eventData?.published !== true) {
          return null;
        }
        return { id: eventId, ...eventData };
      }
    }));

    // remove null values
    favoriteEvents = favoriteEvents.filter(event => event !== null && event !== undefined);

    // convert firebase datestamp to JavaScript Date objects
    favoriteEvents.forEach(event => {
      event.date = event?.date?.toDate();
      if (event?.endDate?.toDate) {
        event.endDate = event.endDate.toDate();
      }
    });

    return favoriteEvents;
    
  } catch (error) {
    console.error('Error fetching favorite events:', error);
    throw error;
  }
};

export const getTotalEvents = async (cities = []) => {
  try {
    let eventsQuery = collection(db, "events");

    // Apply city filter if provided
    if (cities && cities.length > 0) {
      eventsQuery = query(eventsQuery, where("location", "in", cities));
    }

    const snapshot = await getCountFromServer(eventsQuery);
    return snapshot.data().count; // Return the total number of documents
  } catch (error) {
    console.error('Error fetching total events:', error);
    throw error;
  }
};

const DISTANCE_DEFAULT = 20; // 20 km;

export const loadEvents = async (
  city = null, 
  distance = DISTANCE_DEFAULT, 
  startingDate = new Date(new Date().setHours(23,59,0,0)),
  useCache = true,
  restrictToCity = false
) => {
  try {
    console.log('Fetching events...');
    let convertedEventsData = [];
    const shouldUseCache = Boolean(useCache && !city);

    if (shouldUseCache) {
      const cachedEvents = getCachedEvents();
      if (cachedEvents && cachedEvents.length > 0) {
        console.log('Using cached events data');
        convertedEventsData = cachedEvents
          .filter(event => event !== null && event !== undefined)
          .map(event => ({
            ...event,
            date: event.date ? new Date(event.date) : null,
            endDate: event.endDate ? new Date(event.endDate) : null,
            publishedUntil: toDateOrNull(event.publishedUntil)
          }));

        // Process city parameter for cached events
        convertedEventsData = await processCityParameter(convertedEventsData, city, distance, restrictToCity);

        // Filter for upcoming events
        const upcomingEvents = convertedEventsData
          .filter(event => isUpcomingEvent(event, startingDate));
        
        // Apply sorting: if city provided, sort by distance first, then by nearest endDate (fallback to date)
        if (city) {
          console.log('Sorting by searched city, distance and date... : ', upcomingEvents);
          sortEventsForCitySearch(upcomingEvents, city);
        } else {
          // If no city, just sort by endDate (nearest future first, fallback to date)
          upcomingEvents.sort(compareEventsByDefaultOrder);
        }

        logEventsSearchSummary({
          city,
          distance,
          restrictToCity,
          useCache: shouldUseCache,
          totalAfterFilters: upcomingEvents.length
        });
        
        console.log('Upcoming events:', upcomingEvents);
        return { eventsData: upcomingEvents };
      }
    }

    // Fetch events from Firestore if no valid cache or useCache is false
    // Query 1: upcoming precise-date events
    const eventsQuery = query(
      collection(db, "events"),
      where("published", "==", true),
      where("endDate", ">=", startingDate),
      orderBy("endDate", "asc"),
      orderBy("date", "asc")
    );

    // Query 2: non-precise notices still published (recurring notices, announcements, donations)
    const nonPreciseQuery = query(
      collection(db, "events"),
      where("published", "==", true),
      where("hasPreciseDate", "==", false)
    );

    const [querySnapshot, nonPreciseSnapshot] = await Promise.all([
      getDocs(eventsQuery),
      getDocs(nonPreciseQuery)
    ]);

    const eventsMap = new Map();
    [...querySnapshot.docs, ...nonPreciseSnapshot.docs].forEach(doc => {
      const data = doc.data();
      eventsMap.set(doc.id, {
        id: doc.id,
        ...data,
        date: toDateOrNull(data.date),
        endDate: toDateOrNull(data.endDate),
        publishedUntil: toDateOrNull(data.publishedUntil)
      });
    });

    const eventsData = [...eventsMap.values()];

    writeEventsCache(eventsData);

    convertedEventsData = eventsData.map(event => ({
      ...event,
      date: new Date(event.date),
      endDate: event.endDate ? new Date(event.endDate) : null,
      publishedUntil: toDateOrNull(event.publishedUntil)
    }));

    // Process city parameter for Firestore events
    convertedEventsData = await processCityParameter(convertedEventsData, city, distance, restrictToCity);

    // Filter for upcoming events (use endDate when present, otherwise date)
    convertedEventsData = convertedEventsData.filter(event =>
      isUpcomingEvent(event, startingDate)
    );

    // Apply sorting: if city provided, sort by distance first, then by nearest endDate (fallback to date)
    if (city) {
      sortEventsForCitySearch(convertedEventsData, city);
    } else {
      // If no city, just sort by endDate (nearest future first, fallback to date)
      convertedEventsData.sort(compareEventsByDefaultOrder);
    }

    logEventsSearchSummary({
      city,
      distance,
      restrictToCity,
      useCache: false,
      totalAfterFilters: convertedEventsData.length
    });

    return { eventsData: convertedEventsData };

  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

export const loadEvent = async (eventId) => {
  // Check if data is available in cache
  let cachedData = null;
  try {
    cachedData = localStorage.getItem(EVENTS_CACHE_KEY);
  } catch (error) {
    cachedData = null;
  }
  
  if (cachedData) {
    const { eventsData, timestamp } = JSON.parse(cachedData);
    
    if (isCacheValid(timestamp)) {
      console.log('Using cached event data');
      const eventData = eventsData.find(event => event.id === eventId);
      
      if (eventData) {
        if (eventData?.published !== true) {
          throw new Error(`Event with ID ${eventId} is not published`);
        }
        return { 
          ...eventData, 
          date: new Date(eventData.date),
          endDate: eventData.endDate ? new Date(eventData.endDate) : null
        };
      }
    }
  }

  // Fetch data from Firestore
  try {
    console.log(`Fetching event with ID: ${eventId}`);
    const eventDoc = await getDoc(doc(db, "events", eventId));
    if (eventDoc.exists()) {
      const eventData = eventDoc.data();
      if (eventData?.published !== true) {
        throw new Error(`Event with ID ${eventId} is not published`);
      }
      // Ensure proper date conversion with fallbacks
      eventData.date = eventData.date?.toDate() || new Date();
      eventData.endDate = eventData.endDate ? eventData.endDate.toDate() : null;

      // Update cache
      let cachedData = null;
      try {
        cachedData = localStorage.getItem(EVENTS_CACHE_KEY);
      } catch (error) {
        cachedData = null;
      }
      let eventsData = [];
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        eventsData = parsedData.eventsData;
      }
      eventsData = eventsData.filter(event => event.id !== eventId);
      eventsData.push({ id: eventId, ...eventData });
      writeEventsCache(eventsData);

      return { id: eventId, ...eventData };
    } else {
      throw new Error(`Event with ID ${eventId} not found`);
    }
  } catch (error) {
    console.error('Error fetching event:', error);
    throw error;
  }
};

export const uploadEventImage = async (image) => {
    const imageRef = ref(storage, `events/${image.name}`);
    await uploadBytes(imageRef, image);
    const imageUrl = await getDownloadURL(imageRef);
    return imageUrl;
}

const cleanEventData = (eventData) => {
  const eventDate = Timestamp.fromDate(new Date(eventData.date));
  const descriptionWithoutLineFeeds = eventData.description.replace(/(\r\n|\n|\r)/gm, '');
  const sanitizedDescription = DOMPurify.sanitize(descriptionWithoutLineFeeds);
  return { sanitizedDescription, eventDate };
}

export const updateEvent = async (eventData) => {
  try {
    if (eventData.image && eventData.image instanceof File) {
      const imageUrl = await uploadEventImage(eventData.image);
      eventData.image = imageUrl;
    }
    
    const { sanitizedDescription, eventDate } = cleanEventData(eventData);
    // If endDate is null, force endDate = date
    const effectiveEndDate = eventData.endDate ? new Date(eventData.endDate) : new Date(eventData.date);
    const endDate = Timestamp.fromDate(effectiveEndDate);

    const eventDocRef = doc(db, 'events', eventData.id);
    await updateDoc(eventDocRef, {
      title: eventData.title,
      description: sanitizedDescription,
      date: eventDate,
      endDate: endDate,
      location: eventData.location,
      category: eventData.category,
      image: eventData.image,
      updatedAt: Timestamp.fromDate(new Date()),
      published: true
    });

    return { success: true };

  } catch (error) {
    console.error('Error saving event:', error);
    return { success: false, error };
  }
};

export const addEvent = async (eventData) => {
  try {
    if (eventData.image && eventData.image instanceof File) {
      eventData.image = await uploadEventImage(eventData.image);
    }

    // Convert date to Firebase Timestamp
    const { sanitizedDescription, eventDate } = cleanEventData(eventData);
    const endDate = eventData.endDate ? Timestamp.fromDate(new Date(eventData.endDate)) : null;

    // Save event data to Firestore
    await addDoc(collection(db, 'events'), {
      title: eventData.title,
      description: sanitizedDescription,
      date: eventDate,
      endDate: endDate,
      location: eventData.location,
      category: eventData.category,
      image: eventData.image,
      speaker: eventData.speaker,
      organizer: eventData.organizer,
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
      published: true
    });

    return { success: true };
  } catch (error) {
    console.error('Error creating event: ', error);
    return { success: false, error };
  }
};

export const emptyEventsCache = () => {
  localStorage.removeItem(EVENTS_CACHE_KEY);
};

export const deleteEvent = async (eventId) => {
  try {
    await deleteDoc(doc(db, 'events', eventId));
    emptyEventsCache();

  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};